import { Link } from "react-router-dom";
import { ArrowRight, Check, FileText, Users, Sparkles, ClipboardList, UserCheck, ListFilter, CalendarDays, LineChart } from "lucide-react";
import { STAGES } from "@/data/content";

export function HowItWorks() {
  const steps = [["Create your profile", "Add your details, skills and preferences"], ["Discover matching jobs", "Get relevant opportunities across India"], ["Apply and track", "Apply easily and track your application status"]];
  return (
    <section className="navy-grid text-white" data-testid="how-it-works">
      <div className="container-x grid items-center gap-10 py-14 lg:grid-cols-[1fr_2fr]">
        <div>
          <h2 className="text-3xl font-extrabold leading-tight sm:text-4xl">Your profile.<br />Your opportunities.</h2>
          <p className="mt-3 text-sm text-white/70">A simple process to get you from search to success.</p>
        </div>
        <ol className="relative grid gap-8 sm:grid-cols-3">
          <span className="absolute left-[16%] right-[16%] top-6 hidden h-px bg-white/25 sm:block" />
          {steps.map(([t, s], i) => (
            <li key={t} className="relative text-center" data-testid={`how-step-${i + 1}`}>
              <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-[#00B94F] text-lg font-bold ring-8 ring-[#0B1B2B]">{i + 1}</span>
              <p className="mt-4 font-semibold">{t}</p>
              <p className="mt-1 text-xs text-white/65">{s}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function CareerStages() {
  return (
    <section className="container-x py-16" data-testid="career-stages">
      <div className="text-center">
        <h2 className="h-section">Built for every career stage</h2>
        <p className="mt-2 text-sm text-slate-500">Whether you're starting out or aiming higher, Jobfixnow is for you.</p>
      </div>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {STAGES.map(([t, img, pts, to]) => (
          <div key={t} className="card-hover grid grid-cols-[1fr_1.1fr] overflow-hidden" data-testid={`stage-${t.split(" ")[0].toLowerCase()}`}>
            <img src={img} alt={t} width="400" height="600" loading="lazy" decoding="async" className="h-full min-h-[190px] w-full object-cover" />
            <div className="flex flex-col p-4">
              <p className="text-sm font-bold leading-tight">{t}</p>
              <ul className="mt-3 flex-1 space-y-1.5">{pts.map((p) => <li key={p} className="flex gap-1.5 text-xs text-slate-600"><Check size={14} className="shrink-0 text-[#00B94F]" />{p}</li>)}</ul>
              <Link to={to} className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#009a41]" aria-label={`Explore jobs for ${t}`}>Explore Now <ArrowRight size={12} /></Link>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ForEmployersBand({ H = "h2" }) {
  const feats = [[FileText, "Post job openings"], [Users, "Access quality candidates"], [Sparkles, "Smart skill matching"], [ClipboardList, "Manage applications easily"]];
  return (
    <section className="navy-grid relative overflow-hidden text-white" data-testid="employers-band">
      <div className="container-x grid items-center gap-10 py-14 lg:grid-cols-[1.2fr_1fr_0.9fr]">
        <div>
          <span className="rounded-full bg-[#00B94F]/20 px-3 py-1 text-[11px] font-semibold tracking-wider text-[#00B94F]">FOR EMPLOYERS</span>
          <H className="mt-4 text-3xl font-extrabold sm:text-4xl">Meet Your Next Great Hire.</H>
          <p className="mt-3 max-w-md text-sm text-white/70">Find the right talent for your team. Publish roles, review applications and connect with candidates across India.</p>
          <Link to="/signup?role=employer" className="btn-green mt-6" data-testid="employers-post-job-btn">Post a Job <ArrowRight size={16} /></Link>
        </div>
        <img src="/images/employer.webp" width="800" height="514" loading="lazy" decoding="async" alt="Recruiter reviewing candidates" className="h-64 w-full rounded-2xl object-cover ring-4 ring-[#00B94F]/30" />
        <ul className="space-y-3 rounded-xl bg-white p-5 text-[#0B1B2B]">
          {feats.map(([I, t]) => <li key={t} className="flex items-center gap-3 text-sm font-medium"><I size={18} className="text-[#00B94F]" />{t}</li>)}
        </ul>
      </div>
    </section>
  );
}

export function ForCollegesBand({ H = "h2" }) {
  const feats = [[UserCheck, "Student profiles"], [ListFilter, "Eligibility matching"], [CalendarDays, "Campus drives"], [LineChart, "Placement tracking"]];
  return (
    <section className="bg-[#EAF8EF]" data-testid="colleges-band">
      <div className="container-x grid items-center gap-10 py-14 lg:grid-cols-[1.2fr_1fr_0.8fr]">
        <div>
          <span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold tracking-wider text-[#009a41]">FOR COLLEGES & PLACEMENT CELLS</span>
          <H className="mt-4 text-3xl font-extrabold sm:text-4xl">Bring Opportunities to Your Campus.</H>
          <p className="mt-3 max-w-md text-sm text-slate-600">Connect your students with top employers, manage campus drives and track placement progress all in one place.</p>
          <Link to="/signup?role=college" className="btn-green mt-6" data-testid="colleges-partner-btn">Partner with Us <ArrowRight size={16} /></Link>
        </div>
        <img src="/images/college.webp" width="800" height="533" loading="lazy" decoding="async" alt="College students on campus" className="h-64 w-full rounded-2xl object-cover" />
        <ul className="space-y-4">
          {feats.map(([I, t]) => <li key={t} className="flex items-center gap-3 text-sm font-semibold"><span className="grid h-8 w-8 place-items-center rounded-lg bg-white text-[#00B94F]"><I size={16} /></span>{t}</li>)}
        </ul>
      </div>
    </section>
  );
}
